<template>
  <div class="wc-sidebar d-flex flex-column" :class="[{ 'wc-thick-lines': isThickerLines }, isDarkMode ? 'theme--dark' : 'theme--light']">
    <div class="wc-sb-header pt-4 px-4 pb-0">
      <div class="d-flex align-center justify-space-between mb-4">
        <div class="d-flex align-center" style="gap: 12px">
          <div
            class="wc-sb-icon-box"
            style="background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(0, 0, 0, 0)); border: 1px solid rgba(76, 175, 80, 0.3)">
            <v-icon small color="primary">mdi-view-dashboard-outline</v-icon>
          </div>
          <span class="wc-sb-title" style="font-size: 1.1rem; font-weight: 700; letter-spacing: 0.5px">Vorlagen</span>
        </div>
        <div class="d-flex" style="gap: 8px">
          <v-btn
            v-if="activeTab !== 0"
            icon
            @click="editMode = !editMode"
            :color="editMode ? 'primary' : ''"
            class="wc-sb-add"
            elevation="0"
            style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)">
            <v-icon small>{{ editMode ? "mdi-check" : "mdi-pencil-outline" }}</v-icon>
          </v-btn>
          <v-btn
            v-if="hasAppointmentManagementRight"
            icon
            @click="$emit('create')"
            class="wc-sb-add"
            elevation="0"
            style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Premium Segmented Control (Horizontal Flex) -->
      <div class="wc-sb-premium-tabs mb-2 mt-2">
        <div class="wc-sb-tab" :class="{ active: activeTab === 0 }" @click="activeTab = 0">
          <v-icon small class="wc-sb-tab-icon">mdi-checkbox-marked-circle-outline</v-icon>
          <span class="wc-sb-tab-text">Ämtli</span>
        </div>
        <div class="wc-sb-tab" :class="{ active: activeTab === 1 }" @click="activeTab = 1">
          <v-icon small class="wc-sb-tab-icon">mdi-clipboard-text</v-icon>
          <span class="wc-sb-tab-text">Aufgabe</span>
        </div>
        <div class="wc-sb-tab" :class="{ active: activeTab === 2 }" @click="activeTab = 2">
          <v-icon small class="wc-sb-tab-icon">mdi-calendar-star</v-icon>
          <span class="wc-sb-tab-text">Termin</span>
        </div>
        <div class="wc-sb-tab" :class="{ active: activeTab === 3 }" @click="activeTab = 3">
          <v-icon small class="wc-sb-tab-icon">mdi-account-clock</v-icon>
          <span class="wc-sb-tab-text">Info</span>
        </div>
      </div>

      <!-- Suchfeld -->
      <div class="px-1 mt-2">
        <v-text-field
          v-model="searchQuery"
          placeholder="Suchen…"
          dense
          solo
          flat
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          class="wc-sb-search"
          :dark="isDarkMode"
          :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'"
          @click:clear="searchQuery = ''"></v-text-field>
      </div>
    </div>

    <div class="wc-sb-body custom-scrollbar flex-grow-1 pa-4 overflow-y-auto" style="min-height: 0">
      <!-- Suchergebnisse (Wird angezeigt, wenn die Suchanfrage nicht leer ist) -->
      <div v-if="searchQuery && searchQuery.trim()" class="wc-sb-search-results-container">
        <!-- Suchinfo -->
        <div class="wc-sb-search-info mb-2 d-flex align-center justify-space-between px-1">
          <span class="text-caption font-weight-bold" style="color: var(--text-muted)"> Suchergebnisse für "{{ searchQuery }}" </span>
          <v-btn text x-small color="primary" @click="searchQuery = ''" style="text-transform: none; min-width: 0; padding: 0; height: auto">
            Suche löschen
          </v-btn>
        </div>

        <!-- Gruppe: Ämtli -->
        <div v-if="hasAmtliMatches" class="wc-sb-search-group mb-3">
          <div class="wc-sb-search-group-header d-flex align-center mb-2">
            <v-icon small class="mr-2" color="success">mdi-checkbox-marked-circle-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold success--text">Ämtli</span>
          </div>

          <!-- Untergruppe: Tägliche Ämtli -->
          <div v-if="filteredDailyTasks.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: #66bb6a; opacity: 0.8; font-size: 0.7rem">Tägliche Ämtli</div>
            <div
              v-for="tpl in filteredDailyTasks"
              :key="'search-daily-' + tpl.id"
              class="wc-sb-card wc-sb-card-nodrag mb-2"
              :class="{ 'wc-sb-card-highlight': !tpl.isAssigned }"
              :style="{
                '--card-color': tpl.color || '#4CAF50',
                opacity: tpl.isAssigned ? 0.55 : 1,
                cursor: tpl.isAssigned ? 'default' : 'pointer',
              }"
              @click="!tpl.isAssigned && !editMode && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title" :style="{ textDecoration: tpl.isAssigned ? 'line-through' : 'none' }">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta font-weight-bold" style="color: #66bb6a">
                  <v-icon v-if="!tpl.isManualDaily" x-small color="#66bb6a" class="mr-1">mdi-clock-outline</v-icon>{{ tpl.time }}
                </div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.templateId || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  :frequency="tpl.frequency || 'daily'"
                  :exact-time="tpl.assignedTime || ''"
                  :is-dark-mode="isDarkMode" />
              </div>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Untergruppe: Manuelle Ämtli bei Bedarf -->
          <div v-if="manualTaskTemplates.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: #26a69a; opacity: 0.85; font-size: 0.7rem">Bei Bedarf</div>
            <div
              v-for="tpl in manualTaskTemplates"
              :key="'search-manual-tpl-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{
                '--card-color': tpl.color || '#26A69A',
                cursor: isManualTaskPlannedToday(tpl) ? 'default' : 'pointer',
                opacity: isManualTaskPlannedToday(tpl) ? 0.55 : 1,
              }"
              :draggable="!editMode && !isManualTaskPlannedToday(tpl)"
              @dragstart="!editMode && !isManualTaskPlannedToday(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isManualTaskPlannedToday(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-hand-pointing-right" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title" :style="{ textDecoration: isManualTaskPlannedToday(tpl) ? 'line-through' : 'none' }">
                  {{ tpl.title }}
                </div>
                <div class="wc-sb-card-meta">{{ isManualTaskPlannedToday(tpl) ? "Heute eingeplant" : "Manuell einfügen" }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="once"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isManualTaskPlannedToday(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Untergruppe: Tägliche Ämtli Vorlagen -->
          <div v-if="dailyTaskTemplates.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: #42a5f5; opacity: 0.8; font-size: 0.7rem">Tägliche Ämtli Vorlagen</div>
            <div
              v-for="tpl in dailyTaskTemplates"
              :key="'search-daily-tpl-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || '#2196F3', cursor: 'pointer' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Ämtli Vorlage</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="daily"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Untergruppe: Wöchentliche Ämtli -->
          <div v-if="weeklyTaskTemplates.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: #ab47bc; opacity: 0.8; font-size: 0.7rem">Wöchentliche Ämtli</div>
            <div
              v-for="tpl in weeklyTaskTemplates"
              :key="'search-weekly-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :class="{ 'wc-sb-card-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sb-card-warning': getTaskUrgencyLevel(tpl) === 1 }"
              :style="{
                '--card-color': tpl.color || '#AB47BC',
                cursor: isTaskTargetPlanned(tpl) ? 'default' : 'pointer',
                opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
              }"
              :draggable="!editMode && !isTaskTargetPlanned(tpl)"
              @dragstart="!editMode && !isTaskTargetPlanned(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isTaskTargetPlanned(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar-week" }}</v-icon>
              </div>
              <div class="wc-sb-card-content" style="min-width: 0">
                <div class="d-flex align-center justify-space-between" style="gap: 8px">
                  <div class="wc-sb-card-title text-truncate" :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none' }">
                    {{ tpl.title }}
                  </div>
                  <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sb-urgency-badge urgent">FÄLLIG</div>
                  <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sb-urgency-badge warning">BALD</div>
                </div>
                <div class="wc-sb-card-meta mt-1">{{ getTaskCountString(tpl, "weekly") }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="weekly"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isTaskTargetPlanned(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Untergruppe: Monatliche Ämtli -->
          <div v-if="monthlyTaskTemplates.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: #00897b; opacity: 0.8; font-size: 0.7rem">Monatliche Ämtli</div>
            <div
              v-for="tpl in monthlyTaskTemplates"
              :key="'search-monthly-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :class="{ 'wc-sb-card-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sb-card-warning': getTaskUrgencyLevel(tpl) === 1 }"
              :style="{
                '--card-color': tpl.color || '#00897B',
                cursor: isTaskTargetPlanned(tpl) ? 'default' : 'pointer',
                opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
              }"
              :draggable="!editMode && !isTaskTargetPlanned(tpl)"
              @dragstart="!editMode && !isTaskTargetPlanned(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isTaskTargetPlanned(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar-month" }}</v-icon>
              </div>
              <div class="wc-sb-card-content" style="min-width: 0">
                <div class="d-flex align-center justify-space-between" style="gap: 8px">
                  <div class="wc-sb-card-title text-truncate" :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none' }">
                    {{ tpl.title }}
                  </div>
                  <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sb-urgency-badge urgent">FÄLLIG</div>
                  <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sb-urgency-badge warning">BALD</div>
                </div>
                <div class="wc-sb-card-meta mt-1">{{ getTaskCountString(tpl, "monthly") }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="monthly"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isTaskTargetPlanned(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="primary lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Gruppe: Aufgaben -->
        <div v-if="taskTemplates.length > 0" class="wc-sb-search-group mb-3">
          <div class="wc-sb-search-group-header d-flex align-center mb-2">
            <v-icon small class="mr-2" color="warning">mdi-clipboard-text</v-icon>
            <span class="text-subtitle-2 font-weight-bold warning--text">Aufgaben</span>
          </div>
          <div
            v-for="tpl in taskTemplates"
            :key="'search-task-' + (tpl._id || tpl.id)"
            class="wc-sb-card mb-2"
            :style="{ '--card-color': tpl.color || '#FF9800', cursor: 'pointer' }"
            :draggable="!editMode"
            @dragstart="!editMode && onDragStart($event, tpl)"
            @dragend="dragging = null"
            @click="!editMode && $emit('template-click', tpl)">
            <div class="wc-sb-card-icon">
              <v-icon small color="white">{{ tpl.icon || "mdi-clipboard-text" }}</v-icon>
            </div>
            <div class="wc-sb-card-content">
              <div class="wc-sb-card-title">{{ tpl.title }}</div>
              <div class="wc-sb-card-meta">Aufgabe</div>
            </div>
            <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
              <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                ><v-icon small>mdi-delete</v-icon></v-btn
              >
            </div>
          </div>
        </div>

        <!-- Gruppe: Termine -->
        <div v-if="hasTerminMatches" class="wc-sb-search-group mb-3">
          <div class="wc-sb-search-group-header d-flex align-center mb-2">
            <v-icon small class="mr-2" color="info">mdi-calendar-star</v-icon>
            <span class="text-subtitle-2 font-weight-bold info--text">Termine</span>
          </div>

          <!-- Trainings & Checkups -->
          <div v-if="filteredTrainingTemplates.length > 0" class="mb-2">
            <div
              class="text-overline mb-1 pl-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Trainings & Checkups
            </div>
            <div
              v-for="tpl in filteredTrainingTemplates"
              :key="'search-train-' + tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <!-- Verträge & Management -->
          <div v-if="filteredContractTemplates.length > 0" class="mb-2">
            <div
              class="text-overline mb-1 pl-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Verträge & Management
            </div>
            <div
              v-for="tpl in filteredContractTemplates"
              :key="'search-contract-' + tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Aktion</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <!-- Schulung & Teamsitzung -->
          <div v-if="filteredSchulungTemplates.length > 0" class="mb-2">
            <div
              class="text-overline mb-1 pl-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Schulung & Teamsitzung
            </div>
            <div
              v-for="tpl in filteredSchulungTemplates"
              :key="'search-schulung-' + tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <!-- Termine -->
          <div v-if="filteredStandardAppointmentTemplates.length > 0" class="mb-2">
            <div
              class="text-overline mb-1 pl-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Termine
            </div>
            <div
              v-for="tpl in filteredStandardAppointmentTemplates"
              :key="'search-std-app-' + tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <!-- Eigene Vorlagen -->
          <div v-if="customAppointmentTemplates.length > 0" class="mb-2">
            <div class="text-overline mb-1 pl-1" style="color: var(--v-primary-base); opacity: 0.8">Eigene Vorlagen</div>
            <div
              v-for="tpl in customAppointmentTemplates"
              :key="'search-custom-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || 'var(--v-primary-base)' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Gruppe: Infos -->
        <div v-if="unsupervisedTemplates.length > 0" class="wc-sb-search-group mb-3">
          <div class="wc-sb-search-group-header d-flex align-center mb-2">
            <v-icon small class="mr-2" color="purple">mdi-account-clock</v-icon>
            <span class="text-subtitle-2 font-weight-bold purple--text">Infos</span>
          </div>
          <div
            v-for="tpl in unsupervisedTemplates"
            :key="'search-info-' + (tpl._id || tpl.id)"
            class="wc-sb-card mb-2"
            :style="{ '--card-color': tpl.color || '#9C27B0', cursor: 'pointer' }"
            :draggable="!editMode"
            @dragstart="!editMode && onDragStart($event, tpl)"
            @dragend="dragging = null"
            @click="!editMode && $emit('template-click', tpl)">
            <div class="wc-sb-card-icon">
              <v-icon small color="white">{{ tpl.icon || "mdi-account-clock" }}</v-icon>
            </div>
            <div class="wc-sb-card-content">
              <div class="wc-sb-card-title">{{ tpl.title }}</div>
              <div class="wc-sb-card-meta">Info</div>
            </div>
            <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            <div v-if="editMode && !tpl.isDefault && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
              <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                ><v-icon small>mdi-delete</v-icon></v-btn
              >
            </div>
          </div>
        </div>

        <!-- Keine Treffer -->
        <div v-if="!hasAnyMatches" class="text-center mt-10 opacity-60 py-8">
          <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-magnify-close</v-icon>
          <div style="font-size: 0.95rem; font-weight: 600">Keine Vorlagen gefunden</div>
          <div class="text-caption mt-1" style="color: var(--text-muted)">Versuche es mit einem anderen Suchbegriff.</div>
        </div>
      </div>

      <!-- Normaler Tab-Inhalt (Wenn keine Suche aktiv ist) -->
      <template v-else>
        <!-- TAB 0: ÄMTLI -->
        <div v-show="activeTab === 0">
          <!-- Automatische Zuweisung & Dashboard -->
          <div class="px-1 mb-4 d-flex" style="gap: 8px">
            <v-btn
              color="primary"
              class="rounded-lg flex-grow-1"
              depressed
              @click="$emit('auto-assign-tasks-click')"
              style="text-transform: none; letter-spacing: 0; font-weight: 600; font-size: 0.85rem">
              <v-icon left small>mdi-cog-outline</v-icon>
              Ämtli-Einstellungen
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  class="rounded-lg px-0"
                  style="min-width: 44px; max-width: 44px"
                  depressed
                  v-bind="attrs"
                  v-on="on"
                  @click="$emit('open-tasks-dashboard-click')">
                  <v-icon>mdi-view-dashboard</v-icon>
                </v-btn>
              </template>
              <span>Ämtli-Dashboard & Auswertung</span>
            </v-tooltip>
          </div>

          <!-- Tägliche Ämtli (Daily Tasks) -->
          <div class="wc-sb-section mb-8" v-if="filteredDailyTasks.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #66bb6a; opacity: 0.9">Tägliche Ämtli</div>
            <div
              v-for="tpl in filteredDailyTasks"
              :key="tpl.id"
              class="wc-sb-card wc-sb-card-nodrag mb-2"
              :class="{ 'wc-sb-card-highlight': !tpl.isAssigned }"
              :style="{
                '--card-color': tpl.color || '#4CAF50',
                opacity: tpl.isAssigned ? 0.55 : 1,
                cursor: tpl.isAssigned ? 'default' : 'pointer',
              }"
              @click="!tpl.isAssigned && !editMode && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title" :style="{ textDecoration: tpl.isAssigned ? 'line-through' : 'none' }">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta font-weight-bold" style="color: #66bb6a">
                  <v-icon v-if="!tpl.isManualDaily" x-small color="#66bb6a" class="mr-1">mdi-clock-outline</v-icon>{{ tpl.time }}
                </div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.templateId || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  :frequency="tpl.frequency || 'daily'"
                  :exact-time="tpl.assignedTime || ''"
                  :is-dark-mode="isDarkMode" />
              </div>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>
          <div
            v-if="
              filteredDailyTasks.length === 0 &&
              manualTaskTemplates.length === 0 &&
              dailyTaskTemplates.length === 0 &&
              weeklyTaskTemplates.length === 0 &&
              monthlyTaskTemplates.length === 0
            "
            class="text-center mt-10 opacity-40">
            <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-calendar-blank-outline</v-icon>
            <div style="font-size: 0.85rem">Keine Ämtli vorhanden.</div>
          </div>

          <!-- Manuelle Ämtli-Vorlagen, die ohne Automatik bei Bedarf eingefügt werden -->
          <div class="wc-sb-section mb-8 mt-6" v-if="manualTaskTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #26a69a; opacity: 0.9">Bei Bedarf</div>
            <div
              v-for="tpl in manualTaskTemplates"
              :key="'template-manual-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{
                '--card-color': tpl.color || '#26A69A',
                cursor: isManualTaskPlannedToday(tpl) ? 'default' : 'pointer',
                opacity: isManualTaskPlannedToday(tpl) ? 0.55 : 1,
              }"
              :draggable="!editMode && !isManualTaskPlannedToday(tpl)"
              @dragstart="!editMode && !isManualTaskPlannedToday(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isManualTaskPlannedToday(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-hand-pointing-right" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title" :style="{ textDecoration: isManualTaskPlannedToday(tpl) ? 'line-through' : 'none' }">
                  {{ tpl.title }}
                </div>
                <div class="wc-sb-card-meta">{{ isManualTaskPlannedToday(tpl) ? "Heute eingeplant" : "Manuell einfügen" }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="once"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isManualTaskPlannedToday(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Tägliche Aufgaben Vorlagen (Tägliche Ämtli Vorlagen) -->
          <div class="wc-sb-section mb-8 mt-6" v-if="dailyTaskTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #42a5f5; opacity: 0.8">Tägliche Ämtli (Vorlagen)</div>
            <div
              v-for="tpl in dailyTaskTemplates"
              :key="'template-daily-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || '#2196F3', cursor: 'pointer' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Ämtli Vorlage</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="daily"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Wöchentliche Aufgaben -->
          <div class="wc-sb-section mb-8 mt-6" v-if="weeklyTaskTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #ab47bc; opacity: 0.8">Wöchentliche Ämtli</div>
            <div
              v-for="tpl in weeklyTaskTemplates"
              :key="tpl._id || tpl.id"
              class="wc-sb-card mb-2"
              :class="{ 'wc-sb-card-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sb-card-warning': getTaskUrgencyLevel(tpl) === 1 }"
              :style="{
                '--card-color': tpl.color || '#AB47BC',
                cursor: isTaskTargetPlanned(tpl) ? 'default' : 'pointer',
                opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
              }"
              :draggable="!editMode && !isTaskTargetPlanned(tpl)"
              @dragstart="!editMode && !isTaskTargetPlanned(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isTaskTargetPlanned(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar-week" }}</v-icon>
              </div>
              <div class="wc-sb-card-content" style="min-width: 0">
                <div class="d-flex align-center justify-space-between" style="gap: 8px">
                  <div class="wc-sb-card-title text-truncate" :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none' }">
                    {{ tpl.title }}
                  </div>
                  <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sb-urgency-badge urgent">FÄLLIG</div>
                  <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sb-urgency-badge warning">BALD</div>
                </div>
                <div class="wc-sb-card-meta mt-1">{{ getTaskCountString(tpl, "weekly") }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="weekly"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isTaskTargetPlanned(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>

          <!-- Monatliche Aufgaben -->
          <div class="wc-sb-section mb-8 mt-6" v-if="monthlyTaskTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #00897b; opacity: 0.8">Monatliche Ämtli</div>
            <div
              v-for="tpl in monthlyTaskTemplates"
              :key="tpl._id || tpl.id"
              class="wc-sb-card mb-2"
              :class="{ 'wc-sb-card-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sb-card-warning': getTaskUrgencyLevel(tpl) === 1 }"
              :style="{
                '--card-color': tpl.color || '#00897B',
                cursor: isTaskTargetPlanned(tpl) ? 'default' : 'pointer',
                opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
              }"
              :draggable="!editMode && !isTaskTargetPlanned(tpl)"
              @dragstart="!editMode && !isTaskTargetPlanned(tpl) && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && !isTaskTargetPlanned(tpl) && $emit('task-assign', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar-month" }}</v-icon>
              </div>
              <div class="wc-sb-card-content" style="min-width: 0">
                <div class="d-flex align-center justify-space-between" style="gap: 8px">
                  <div class="wc-sb-card-title text-truncate" :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none' }">
                    {{ tpl.title }}
                  </div>
                  <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sb-urgency-badge urgent">FÄLLIG</div>
                  <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sb-urgency-badge warning">BALD</div>
                </div>
                <div class="wc-sb-card-meta mt-1">{{ getTaskCountString(tpl, "monthly") }}</div>
                <work-calendar-task-assignment-info
                  :template-id="tpl._id || tpl.id"
                  :completions="completions"
                  :current-date="currentDate"
                  frequency="monthly"
                  :is-dark-mode="isDarkMode" />
              </div>
              <v-icon v-if="!editMode && !isTaskTargetPlanned(tpl)" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode" class="d-flex" style="gap: 2px">
                <v-btn icon small color="primary lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="hasAppointmentManagementRight" icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>
        </div>

        <!-- TAB 1: AUFGABE -->
        <div v-show="activeTab === 1">
          <div class="wc-sb-section mb-8 mt-4" v-if="taskTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #ff9800; opacity: 0.8">Aufgaben Vorlagen</div>
            <div
              v-for="tpl in taskTemplates"
              :key="tpl._id || tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || '#FF9800', cursor: 'pointer' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-clipboard-text" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Aufgabe</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>
          <div v-else class="text-center mt-10 opacity-40">
            <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-clipboard-text</v-icon>
            <div style="font-size: 0.85rem">Keine Aufgaben Vorlagen.</div>
          </div>
        </div>

        <!-- TAB 2: TERMINE -->
        <div v-show="activeTab === 2">
          <div class="wc-sb-section mb-8" v-if="filteredTrainingTemplates.length > 0">
            <div
              class="text-overline mb-3 letter-spacing-1 px-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Trainings & Checkups
            </div>
            <div
              v-for="tpl in filteredTrainingTemplates"
              :key="tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <div class="wc-sb-section mb-8" v-if="filteredContractTemplates.length > 0">
            <div
              class="text-overline mb-3 letter-spacing-1 px-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Verträge & Management
            </div>
            <div
              v-for="tpl in filteredContractTemplates"
              :key="tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Aktion</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <div class="wc-sb-section mb-8" v-if="filteredSchulungTemplates.length > 0">
            <div
              class="text-overline mb-3 letter-spacing-1 px-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Schulung & Teamsitzung
            </div>
            <div
              v-for="tpl in filteredSchulungTemplates"
              :key="tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <div class="wc-sb-section mb-8" v-if="filteredStandardAppointmentTemplates.length > 0">
            <div
              class="text-overline mb-3 letter-spacing-1 px-1"
              :style="!isDarkMode ? 'color: rgba(0,0,0,0.65); font-weight: 700;' : 'color: rgba(255,255,255,0.6);'">
              Termine
            </div>
            <div
              v-for="tpl in filteredStandardAppointmentTemplates"
              :key="tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
            </div>
          </div>

          <div class="wc-sb-section mb-8" v-if="customAppointmentTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: var(--v-primary-base); opacity: 0.8">Eigene Vorlagen</div>
            <div
              v-for="tpl in customAppointmentTemplates"
              :key="'custom-' + (tpl._id || tpl.id)"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || 'var(--v-primary-base)' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-calendar" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">{{ tpl.duration }} Min.</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 3">
          <div class="wc-sb-section mb-8 mt-4" v-if="unsupervisedTemplates.length > 0">
            <div class="text-overline mb-3 letter-spacing-1 px-1" style="color: #9c27b0; opacity: 0.8">Info Vorlagen</div>
            <div
              v-for="tpl in unsupervisedTemplates"
              :key="tpl._id || tpl.id"
              class="wc-sb-card mb-2"
              :style="{ '--card-color': tpl.color || '#9C27B0', cursor: 'pointer' }"
              :draggable="!editMode"
              @dragstart="!editMode && onDragStart($event, tpl)"
              @dragend="dragging = null"
              @click="!editMode && $emit('template-click', tpl)">
              <div class="wc-sb-card-icon">
                <v-icon small color="white">{{ tpl.icon || "mdi-account-clock" }}</v-icon>
              </div>
              <div class="wc-sb-card-content">
                <div class="wc-sb-card-title">{{ tpl.title }}</div>
                <div class="wc-sb-card-meta">Info</div>
              </div>
              <v-icon v-if="!editMode" x-small class="wc-sb-card-drag">mdi-drag-variant</v-icon>
              <div v-if="editMode && !tpl.isDefault && hasAppointmentManagementRight" class="d-flex" style="gap: 2px">
                <v-btn icon small color="blue lighten-1" @click.stop="$emit('edit-template', tpl)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="red lighten-1" @click.stop="$emit('delete-template', tpl._id || tpl.id)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </div>
          <div v-else class="text-center mt-10 opacity-40">
            <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-account-clock</v-icon>
            <div style="font-size: 0.85rem">Keine Info Vorlagen.</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import WorkCalendarTaskAssignmentInfo from "./WorkCalendarTaskAssignmentInfo.vue"

export default {
  name: "WorkCalendarSidebar",
  components: {
    WorkCalendarTaskAssignmentInfo,
  },
  // 1. Props wiederhergestellt, um eigene Vorlagen zu empfangen
  props: {
    templates: { type: Array, default: () => [] },
    dailyTasks: { type: Array, default: () => [] },
    completions: { type: Array, default: () => [] },
    currentDate: { type: String, default: "" },
    isThickerLines: { type: Boolean, default: false },
    isDarkMode: { type: Boolean, default: false },
    hasAppointmentManagementRight: { type: Boolean, default: false },
  },
  watch: {
    activeTab(val) {
      this.editMode = false
    },
  },
  data() {
    return {
      editMode: false,
      dragging: null,
      searchQuery: "",
      activeTab: 2, // Start with "Termin" tab by default since it's used most often

      trainingTemplates: [
        {
          id: "w8",
          title: "Beratungstermin",
          icon: "mdi-account-tie",
          color: "#5C6BC0",
          duration: 60,
          type: "appointment",
          specialType: "consultation",
          requiresCustomer: true,
        },
        {
          id: "w1",
          title: "Probetraining",
          icon: "mdi-account-star",
          color: "#F57C00",
          duration: 60,
          type: "appointment",
          specialType: "trial",
          requiresCustomer: true,
        },
        {
          id: "w11d",
          title: "Abschlusstermin 11 Tage",
          icon: "mdi-calendar-check",
          color: "#009688",
          duration: 60,
          type: "appointment",
          specialType: "abschlusstermin_11d",
          requiresCustomer: true,
        },
        {
          id: "w9",
          title: "Trainingsplan",
          icon: "mdi-clipboard-list",
          color: "#0288D1",
          duration: 60,
          type: "appointment",
          specialType: "trainingplan",
          requiresCustomer: true,
        },
        {
          id: "w4",
          title: "Kontrolltermin",
          icon: "mdi-calendar-check",
          color: "#009688",
          duration: 30,
          type: "appointment",
          specialType: "check",
          requiresCustomer: true,
        },
        {
          id: "w2",
          title: "Einführungstraining",
          icon: "mdi-dumbbell",
          color: "#1976D2",
          duration: 60,
          type: "appointment",
          specialType: "intro", // Geändert von workout auf intro
          requiresCustomer: true,
        },
        {
          id: "w2b",
          title: "Ergänzungstraining",
          icon: "mdi-dumbbell",
          color: "#1565C0",
          duration: 30,
          type: "appointment",
          specialType: "followup", // Geändert von workout auf followup
          requiresCustomer: true,
        },
        {
          id: "w2d",
          title: "Personal Training",
          icon: "mdi-run",
          color: "#0D47A1",
          duration: 90,
          type: "appointment",
          specialType: "workout",
          tacServiceType: "personal_training",
          requiresCustomer: true,
        },
        {
          id: "w2e",
          title: "Massage",
          icon: "mdi-spa",
          color: "#8E24AA",
          duration: 45,
          type: "appointment",
          specialType: "massage",
          tacServiceType: "massage",
          requiresCustomer: true,
        },
        {
          id: "w5",
          title: "Milon Einführung",
          icon: "mdi-sync",
          color: "#3F51B5",
          duration: 60,
          type: "appointment",
          specialType: "milon",
          requiresCustomer: true,
        },
        {
          id: "w10",
          title: "Mescan",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w11",
          title: "PelviX",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w6",
          title: "Ernährungsberatung",
          icon: "mdi-food-apple",
          color: "#8BC34A",
          duration: 60,
          type: "appointment",
          specialType: "nutrition",
          requiresCustomer: true,
        },
      ],
      contractTemplates: [
        {
          id: "a1",
          title: "Vertrag erstellen",
          icon: "mdi-file-document-edit",
          color: "#37474F",
          duration: 30,
          type: "appointment",
          specialType: "new_contract",
          requiresCustomer: false,
        },
        {
          id: "a2",
          title: "Upgrade / Anpassung",
          icon: "mdi-star-circle",
          color: "#455A64",
          duration: 30,
          type: "appointment",
          specialType: "upgrade",
          requiresCustomer: true,
        },
        {
          id: "w7",
          title: "Anamnese / Checkup",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
      ],
      schulungTemplates: [
        {
          id: "s1",
          title: "Schulung",
          icon: "mdi-school",
          color: "#E65100",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
        {
          id: "s2",
          title: "Teamsitzung",
          icon: "mdi-account-group",
          color: "#FF9800",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
      ],
      standardAppointmentTemplates: [
        { id: "ap1", title: "Termin", icon: "mdi-calendar", color: "#00963f", duration: 60, type: "appointment", requiresCustomer: true },
      ],
    }
  },
  computed: {
    filteredTrainingTemplates() {
      return this.filterBySearch(this.trainingTemplates)
    },
    filteredContractTemplates() {
      return this.filterBySearch(this.contractTemplates)
    },
    filteredSchulungTemplates() {
      return this.filterBySearch(this.schulungTemplates)
    },
    filteredStandardAppointmentTemplates() {
      return this.filterBySearch(this.standardAppointmentTemplates)
    },
    customAppointmentTemplates() {
      return this.filterBySearch(this.templates.filter((t) => t.category === "termin" || (!t.category && t.type === "appointment")))
    },
    customTaskTemplates() {
      // Alle Ämtli (nur konfigurierte)
      return this.templates.filter((t) => (t.category === "ämtli" || (!t.category && t.type === "task")) && t.enabledInStudio)
    },
    dailyTaskTemplates() {
      return this.filterBySearch(this.customTaskTemplates.filter((t) => !t.frequency || t.frequency === "daily"))
    },
    manualTaskTemplates() {
      return this.filterBySearch(this.customTaskTemplates.filter((t) => t.frequency === "once"))
    },
    weeklyTaskTemplates() {
      return this.filterBySearch(this.customTaskTemplates.filter((t) => t.frequency === "weekly"))
    },
    monthlyTaskTemplates() {
      return this.filterBySearch(this.customTaskTemplates.filter((t) => t.frequency === "monthly"))
    },
    taskTemplates() {
      // Aufgabe
      return this.filterBySearch(this.templates.filter((t) => t.category === "aufgabe"))
    },
    unsupervisedTemplates() {
      // Unbetreut
      const base = this.templates.filter((t) => t.category === "info")
      const defaultPause = {
        id: "pause_default",
        title: "Pause",
        icon: "mdi-pause-circle",
        color: "#ab47bc",
        duration: 30,
        type: "pause",
        category: "info",
        isPause: true,
        isDefault: true,
        specialType: "pause",
      }
      const list = [defaultPause, ...base]
      return this.filterBySearch(list)
    },
    filteredDailyTasks() {
      return this.filterBySearch(this.dailyTasks)
    },
    hasAmtliMatches() {
      return (
        this.filteredDailyTasks.length > 0 ||
        this.manualTaskTemplates.length > 0 ||
        this.dailyTaskTemplates.length > 0 ||
        this.weeklyTaskTemplates.length > 0 ||
        this.monthlyTaskTemplates.length > 0
      )
    },
    hasTerminMatches() {
      return (
        this.filteredTrainingTemplates.length > 0 ||
        this.filteredContractTemplates.length > 0 ||
        this.filteredSchulungTemplates.length > 0 ||
        this.filteredStandardAppointmentTemplates.length > 0 ||
        this.customAppointmentTemplates.length > 0
      )
    },
    hasAnyMatches() {
      return this.hasAmtliMatches || this.taskTemplates.length > 0 || this.hasTerminMatches || this.unsupervisedTemplates.length > 0
    },
  },
  methods: {
    filterBySearch(items) {
      if (!this.searchQuery || !this.searchQuery.trim()) return items
      const q = this.searchQuery.trim().toLowerCase()
      return items.filter((t) => (t.title || "").toLowerCase().includes(q))
    },
    dailyTaskState(tpl) {
      if (tpl && tpl.isAssigned && tpl.assignedTo) return { label: "Eingeplant", color: "#2196f3", icon: "mdi-calendar-check" }
      if (tpl && tpl.isAssigned) {
        return { label: "Eingeplant · ohne Mitarbeiter", color: "#f59e0b", icon: "mdi-account-alert-outline" }
      }
      return { label: "Noch nicht eingeplant", color: "var(--text-muted)", icon: "mdi-calendar-blank-outline" }
    },
    getTaskUrgencyLevel(tpl) {
      if (this.isTaskTargetPlanned(tpl)) return 0
      const refDate = this.currentDate ? this.$moment(this.currentDate) : this.$moment()

      if (tpl.frequency === "weekly") {
        const day = refDate.isoWeekday()
        if (day >= 5) return 2 // Friday, Saturday, Sunday = Urgent
        if (day === 4) return 1 // Thursday = Warning
      } else if (tpl.frequency === "monthly") {
        const daysInMonth = refDate.daysInMonth()
        const currentDay = refDate.date()
        const daysLeft = daysInMonth - currentDay
        if (daysLeft <= 3) return 2 // Last 3 days = Urgent
        if (daysLeft <= 7) return 1 // Last 7 days = Warning
      }
      return 0
    },
    getTaskCountInfo(tpl, frequency) {
      const target = this.getTaskTargetCount(tpl, frequency)
      if (!this.completions) return { current: 0, planned: 0, open: 0, done: 0, target }
      const templateCompletions = this.completions.filter((c) => String(c.template) === String(tpl._id || tpl.id))
      const refDate = this.currentDate ? this.$moment(this.currentDate) : this.$moment()
      let periodEntries = []
      if (frequency === "weekly") {
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "isoWeek"))
      } else if (frequency === "monthly") {
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "month"))
      } else {
        // Tägliche und manuelle/einmalige Vorlagen gelten für genau den
        // ausgewählten Kalendertag.
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "day"))
      }
      periodEntries = this.getEffectiveTaskProgressEntries(periodEntries)
      const scheduledEntries = periodEntries.filter((entry) => !this.isManualTemplateCompletion(entry))
      // Only real calendar entries count as planned. Manual completion records
      // remain visible as details but do not cross out the template.
      const planned = scheduledEntries.length
      const done = periodEntries.filter((entry) => this.isTemplateProgressDone(entry)).length
      const open = scheduledEntries.filter((entry) => !this.isTemplateProgressDone(entry)).length
      return { current: planned, planned, open, done, target }
    },
    isManualTaskPlannedToday(tpl) {
      if (!tpl || !Array.isArray(this.completions)) return false
      const templateId = String(tpl._id || tpl.id || "")
      const referenceDate = this.currentDate ? this.$moment(this.currentDate) : this.$moment()
      return this.completions.some((entry) => {
        if (String(entry.template || "") !== templateId || this.isManualTemplateCompletion(entry)) return false
        return this.$moment(entry.date).isSame(referenceDate, "day")
      })
    },
    getEffectiveTaskProgressEntries(entries) {
      const scheduled = (entries || []).filter((entry) => !this.isManualTemplateCompletion(entry))
      const scheduledDates = new Set(scheduled.map((entry) => String(entry.date || "")))
      const manualByDate = new Map()
      ;(entries || [])
        .filter((entry) => this.isManualTemplateCompletion(entry))
        .forEach((entry) => {
          const dateKey = String(entry.date || "")
          if (!scheduledDates.has(dateKey) && !manualByDate.has(dateKey)) manualByDate.set(dateKey, entry)
        })
      return [...scheduled, ...manualByDate.values()]
    },
    getTaskTargetCount(tpl, frequency) {
      const config = (tpl && tpl.frequencyConfig) || {}
      if (config.daySelectionMode === "all") {
        if (frequency === "weekly" && Array.isArray(config.daysOfWeek) && config.daysOfWeek.length) {
          return new Set(config.daysOfWeek.map(Number).filter((day) => day >= 1 && day <= 7)).size || 1
        }
        if (frequency === "monthly" && Array.isArray(config.daysOfMonth) && config.daysOfMonth.length) {
          return new Set(config.daysOfMonth.map(Number).filter((day) => day >= 1 && day <= 31)).size || 1
        }
      }
      return Math.max(1, Number(tpl && tpl.targetCount) || 1)
    },
    getTaskCountString(tpl, frequency) {
      const { planned, target } = this.getTaskCountInfo(tpl, frequency)
      return `${planned}/${target} eingeplant`
    },
    getAllCompletions(tpl) {
      if (!this.completions) return []
      const refDate = this.currentDate ? this.$moment(this.currentDate) : this.$moment()
      const entries = this.completions
        .filter((c) => String(c.template) === String(tpl._id || tpl.id))
        .filter((c) => {
          if (tpl.frequency === "weekly") return this.$moment(c.date).isSame(refDate, "isoWeek")
          if (tpl.frequency === "monthly") return this.$moment(c.date).isSame(refDate, "month")
          return this.$moment(c.date).isSame(refDate, "day")
        })
      return this.getEffectiveTaskProgressEntries(entries)
        .filter((entry) => !this.isManualTemplateCompletion(entry))
        .sort((a, b) => (a.date > b.date ? -1 : 1))
    },
    isTaskDone(tpl) {
      const { done, open, target } = this.getTaskCountInfo(tpl, tpl.frequency)
      return done >= target && open === 0
    },
    isTaskTargetPlanned(tpl) {
      const { planned, target } = this.getTaskCountInfo(tpl, tpl.frequency)
      return planned >= target
    },
    isManualTemplateCompletion(entry) {
      return Boolean(entry && (entry.completedBy || entry.source === "manual"))
    },
    isTemplateProgressDone(entry) {
      if (!entry) return false
      const status = String(entry.status || "").toLowerCase()
      return entry.isDone === true || Boolean(entry.doneAt) || status === "done" || status === "completed" || this.isManualTemplateCompletion(entry)
    },
    templateProgressState(entry) {
      if (entry && entry.assignedEmployee) {
        return { label: "Eingeplant", color: "#2196f3", icon: "mdi-calendar-check" }
      }
      return { label: "Eingeplant · ohne Mitarbeiter", color: "#f59e0b", icon: "mdi-account-alert-outline" }
    },
    toggleTaskCompletion(tpl) {
      if (this.isTaskDone(tpl)) {
        // Find completion id to delete
        const templateCompletions = this.completions.filter(
          (c) => String(c.template) === String(tpl._id || tpl.id) && this.isManualTemplateCompletion(c),
        )
        const refDate = this.currentDate ? this.$moment(this.currentDate) : this.$moment()
        let toDelete = null
        if (tpl.frequency === "weekly") {
          toDelete = templateCompletions.find((c) => this.$moment(c.date).isSame(refDate, "isoWeek"))
        } else if (tpl.frequency === "monthly") {
          toDelete = templateCompletions.find((c) => this.$moment(c.date).isSame(refDate, "month"))
        }
        if (toDelete) {
          this.$emit("unmark-done", toDelete._id)
        }
      } else {
        // Erstellen
        this.$emit("mark-done", { templateId: tpl._id || tpl.id, date: this.$moment().format("YYYY-MM-DD") })
      }
    },
    onDragStart(e, tpl) {
      try {
        this.dragging = tpl.id || tpl._id
        const dragObj = { ...tpl, create: true }

        if (e.dataTransfer) {
          e.dataTransfer.setData("templateObj", JSON.stringify(dragObj))
          e.dataTransfer.effectAllowed = "copy"
        }

        this.$emit("drag-start", dragObj)

        const targetEl = e.currentTarget || e.target
        if (targetEl && typeof targetEl.querySelector === "function") {
          const dragIcon = targetEl.querySelector(".wc-sb-card-icon")
          if (dragIcon && e.dataTransfer && typeof e.dataTransfer.setDragImage === "function") {
            e.dataTransfer.setDragImage(dragIcon, 16, 16)
          }
        }
      } catch (err) {
        console.error("Error in onDragStart:", err)
      }
    },
  },
}
</script>

<style scoped>
.wc-sidebar {
  overflow: hidden;
  background: var(--bg-sidebar);
  border-left: 1px solid var(--border-light);
}

.wc-sb-header {
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-panel);
}
.wc-sb-icon-box {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
}
.wc-sb-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-main);
}
.wc-sb-add {
  color: var(--icon-muted) !important;
  background: rgba(128, 128, 128, 0.1) !important;
  transition: all 0.2s;
}
.wc-sb-add:hover {
  color: #66bb6a !important;
  background: rgba(76, 175, 80, 0.15) !important;
}

.wc-sb-tabs-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  background: var(--bg-panel);
  border-radius: 12px;
  padding: 4px;
  border: 1px solid var(--border-light);
}
.wc-sb-tab {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.wc-sb-tab.active {
  background: var(--bg-card);
  color: var(--text-main);
  box-shadow: var(--shadow-color) 0px 2px 8px;
}
.wc-sb-tab i {
  color: inherit !important;
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}
.wc-sb-card {
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  user-select: none;
}
.wc-sb-card:hover {
  transform: translateY(-2px);
  border-color: var(--card-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@keyframes wc-pulse-urgent {
  0%,
  100% {
    border-color: rgba(229, 57, 53, 0.8);
    opacity: 1;
  }
  50% {
    border-color: var(--border-light);
    opacity: 0.8;
  }
}

.wc-sb-card-urgent {
  animation: wc-pulse-urgent 2.5s ease-in-out infinite;
  border-color: rgba(229, 57, 53, 0.8);
}

@media (prefers-reduced-motion: reduce) {
  .wc-sb-card-urgent {
    animation: none !important;
  }
}

.wc-sb-card-warning {
  border-color: #fb8c00;
}

.wc-sb-urgency-badge {
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.wc-sb-urgency-badge.urgent {
  background: rgba(229, 57, 53, 0.15);
  color: #ef5350;
  border: 1px solid rgba(229, 57, 53, 0.3);
}

.wc-sb-urgency-badge.warning {
  background: rgba(251, 140, 0, 0.15);
  color: #ffb74d;
  border: 1px solid rgba(251, 140, 0, 0.3);
}

.wc-sb-card:active {
  cursor: grabbing;
}
.wc-sb-card-nodrag {
  cursor: pointer !important;
}
.wc-sb-card-nodrag:active {
  cursor: pointer !important;
  transform: scale(0.98);
}
.wc-sb-card-highlight {
  border-left: 3px solid #66bb6a;
  background: rgba(102, 187, 106, 0.05);
}
.wc-sb-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card-color);
  flex-shrink: 0;
}
.wc-sb-card-content {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}
.wc-sb-card-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wc-sb-card-meta {
  font-size: 0.7rem;
  color: var(--text-muted);
  font-weight: 600;
}
.wc-thick-lines .wc-sb-card-meta {
  font-weight: 800 !important;
  color: var(--text-main) !important;
  font-size: 0.76rem !important;
}
.wc-sidebar.theme--light .wc-thick-lines .wc-sb-card-meta {
  color: #000000 !important;
}
.wc-sb-card-completion {
  font-size: 0.65rem;
  color: var(--text-muted);
  margin-top: 1px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 2px;
  line-height: 1.2;
}
.wc-sb-card-drag {
  color: var(--text-disabled);
  opacity: 0.3;
}

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 3px;
}
.wc-sb-premium-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  background: var(--bg-panel);
  border-radius: 16px;
  border: 1px solid var(--border-medium);
}
.wc-sb-tab {
  flex: 1 1 calc(50% - 6px);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;
}
@media (hover: hover) {
  .wc-sb-tab:hover:not(.active) {
    background: var(--bg-panel-hover);
  }
}
.wc-sb-tab.active {
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  box-shadow: 0 4px 15px var(--shadow-color);
}
.wc-sb-tab-icon {
  color: var(--icon-muted) !important;
  transition: all 0.3s ease;
}
.wc-sb-tab.active .wc-sb-tab-icon {
  color: var(--text-main) !important;
  transform: scale(1.1);
}
.wc-sb-tab-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  transition: all 0.3s ease;
}
.wc-sb-tab.active .wc-sb-tab-text {
  color: var(--text-main);
}

/* Search Input */
.wc-sb-search >>> .v-input__slot {
  min-height: 38px !important;
  border-radius: 10px !important;
  border: 1px solid var(--border-medium) !important;
}
.wc-sb-search >>> input {
  font-size: 0.9rem;
}
.wc-sb-search >>> .v-icon {
  font-size: 18px !important;
  color: var(--text-muted) !important;
}

/* Inactive Sidebar Tabs (Light Mode Contrast) */
.wc-sidebar.theme--light .wc-sb-tab:not(.active) .wc-sb-tab-text {
  color: rgba(0, 0, 0, 0.6) !important;
}
.wc-sidebar.theme--light .wc-sb-tab:not(.active) .wc-sb-tab-icon {
  color: rgba(0, 0, 0, 0.5) !important;
}

/* Search Results styling */
.wc-sb-search-results-container {
  animation: wcSearchFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes wcSearchFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.wc-sb-search-info {
  border-bottom: 1px dashed var(--border-medium);
  padding-bottom: 8px;
}

.wc-sb-search-group {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 8px;
  border: 1px solid var(--border-light);
}

.wc-sidebar.theme--light .wc-sb-search-group {
  background: rgba(0, 0, 0, 0.01);
}

.wc-sb-search-group-header {
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 4px;
  margin-bottom: 8px !important;
}
</style>
